// What the scanner does with what the detector reads (phase 4 §5.3): pure
// functions, tested without a camera.
import { readCode } from '../fieldTypes';

/** The formats asked of the detector: the barcodes of books, films, games and household goods. */
export const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e'];

/**
 * A UPC-E (8 digits: number system 0 or 1, six digits, check) as the UPC-A it
 * stands for, or null. The check digit is the UPC-A's: readCode verifies it.
 */
export const expandUpcE = (digits) => {
  if (!/^[01]\d{7}$/.test(digits)) return null;
  const [ns, d1, d2, d3, d4, d5, d6, check] = digits.split('');
  let body;
  if (d6 <= '2') body = `${d1}${d2}${d6}0000${d3}${d4}${d5}`;
  else if (d6 === '3') body = `${d1}${d2}${d3}00000${d4}${d5}`;
  else if (d6 === '4') body = `${d1}${d2}${d3}${d4}00000${d5}`;
  else body = `${d1}${d2}${d3}${d4}${d5}0000${d6}`;
  return `${ns}${body}${check}`;
};

/**
 * A detector's barcode ({rawValue, format}) as a code the server accepts —
 * {scheme: isbn|ean, value} — or null when it is none (a misread: its check
 * digit fails, it is another kind of barcode).
 */
export const readDetected = (barcode) => {
  if (!barcode || typeof barcode.rawValue !== 'string') return null;
  if (barcode.format && !FORMATS.includes(barcode.format)) return null;
  const raw = barcode.rawValue.trim();
  if (barcode.format === 'upc_e') {
    const upcA = expandUpcE(raw.replace(/\D/g, '').slice(0, 8));
    return upcA ? readCode(upcA) : null;
  }
  return readCode(raw);
};

/** A code as shown: "ISBN 9782070368228", "EAN 3558380077565". */
export const codeLabel = (code) => (code ? `${code.scheme === 'isbn' ? 'ISBN' : 'EAN'} ${code.value}` : '');

/**
 * The two-frame rule (§5.3): a code is accepted once it was read on
 * `frames` consecutive frames (misreads of an EAN-13 almost never repeat),
 * then ignored while it stays in view and for `cooldownMs` after it left —
 * holding a book in front of the camera adds it once.
 *
 *   const confirm = createConfirmer();
 *   confirm.feed(codesOfThisFrame, now) → the accepted code, or null
 */
export function createConfirmer({ frames = 2, cooldownMs = 3000 } = {}) {
  let streaks = new Map();   // value → consecutive frames it was read on
  const seen = new Map();    // value → when an accepted code was last in view
  return {
    feed(codes, now = Date.now()) {
      const next = new Map();
      let accepted = null;
      for (const code of codes || []) {
        if (!code || next.has(code.value)) continue;
        const cooling = seen.has(code.value) && now - seen.get(code.value) < cooldownMs;
        if (cooling) {
          seen.set(code.value, now);   // still in view: the cooldown starts again
          next.set(code.value, 0);
          continue;
        }
        const streak = (streaks.get(code.value) || 0) + 1;
        if (streak >= frames && !accepted) {
          accepted = code;
          seen.set(code.value, now);
          next.set(code.value, 0);
        } else {
          next.set(code.value, streak);
        }
      }
      streaks = next;   // a code missing from this frame starts again from zero
      return accepted;
    },
    /** Forgets every streak and cooldown (another type, the pile emptied). */
    reset() {
      streaks = new Map();
      seen.clear();
    },
  };
}
